import React, { useState } from 'react'
import { Label, LabelProps } from '../../../../components/Label'
import { RoutineType } from '../../../../domain/db/RoutineTypeDB'
import { Avatar, Card, Grid, IconButton, MenuItem, Popover, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../../../services/api'
import { DialogConfirmation } from '../../../../components/DialogConfirmation'
import Iconify from '../../../../components/Iconify'
import { SequenceTypeIcon } from '../../../TrainingBench/components/SequenceTypeIcon'
import { setLimitCharacter } from 'utils'
import useResponsive from 'hooks/useResponsive'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { useAppDispatch } from 'redux/hooks'
import moment from 'moment'

export type AthleteRoutineProps = {
  id: string
  name: string
  level: string
  note?: string
  labels: LabelProps[]
  type: RoutineType
  avatarUrl: string
  startedAt: any
  createdAt: any
}

type Props = {
  items: AthleteRoutineProps[]
  onEdit: (athleteRoutine: any) => void
  onAthleteList: (data: AthleteRoutineProps[]) => void
  onHandleCopy: (athleteRoutine: any) => void
}

export const AthleteRoutineDataList = ({ items, onEdit, onAthleteList, onHandleCopy }: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [open, setOpen] = useState(null)
  const [athleteRoutine, setAthleteRoutine] = useState<any | null>(null)
  const [indexRoutine, setIndexRoutine] = useState<any>()
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const toggleDialog = () => setOpenConfirmation(!openConfirmation)
  const mdUp = useResponsive('up', 'md')
  const dispatch = useAppDispatch()

  const handleOpenMenu = (event: any, athleteRoutine: any, index: number) => {
    setOpen(event.currentTarget)
    setAthleteRoutine(athleteRoutine)
    setIndexRoutine(index)
  }

  const handleCloseMenu = () => {
    setOpen(null)
  }

  const handleGoToTrainings = (item: AthleteRoutineProps) => {
    navigate(`${item.id}/trainings`, { state: item })
  }

  const handleTrainingValidity = (item: any) => {
    let visibleAfterDueDateMessage = t('athlete.trainingValidity.notVisibleAfterDueDateMsg')
    if (item.visibleAfterDueDate) {
      visibleAfterDueDateMessage = t('athlete.trainingValidity.visibleAfterDueDateMsg')
    }
    const validComplement = (item.dueDateAt ? `${t('athlete.trainingValidity.dueDateAtNoEmpty')} ${moment(item.dueDateAt).format('DD/MM/YY')}` : t('athlete.trainingValidity.dueDateAtEmpty'))
    return `${t('athlete.trainingValidity.startedAt')} ${moment(item.startedAt).format('DD/MM/YY')} ${validComplement} - ${visibleAfterDueDateMessage}`
  }

  const handleRemove = async () => {
    try {
      await api.delete(`professional-athlete-routines/${athleteRoutine?.id}`)
      dispatch(showMessage({ message: `${t('routine.delete_success')}` }))
      items.splice(indexRoutine, 1)
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
    onAthleteList(items)
    setOpenConfirmation(false)
  }

  return (
    <>
      <DialogConfirmation
        title={`${t('routine.deleteConfirmation_title')}`}
        message={`${t('routine.deleteConfirmation_messenger', { name: athleteRoutine?.name })}`}
        open={openConfirmation}
        onClose={toggleDialog}
        action={handleRemove}
      />
      {items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((item: any, index: any) => (
        <Card key={index} sx={{ pl: '10px', mb: '10px' }}>
          <Grid padding={1} key={`container_${index}`} container alignItems="center">
            <Grid item xs={10} md={10.7} sx={{ cursor: 'pointer' }} onClick={() => { handleGoToTrainings(item) }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Stack>
                  <Avatar sx={{ width: '60px', height: '60px', bgcolor: theme.palette.primary.main }}>
                    <Iconify color="#FFF" icon="arcticons:home-workouts" width="40px"></Iconify>
                  </Avatar>
                </Stack>
                <Stack spacing={1} padding={1}>
                  <Stack spacing={1} direction="row">
                    <SequenceTypeIcon type={item.type}/>
                    <Typography textAlign={'justify'} margin={0.5} variant="subtitle2" sx={{ textTransform: 'uppercase' }}>{item.name}</Typography>
                  </Stack>
                  <Stack spacing={1} direction="row">
                    <Typography variant="subtitle2">{handleTrainingValidity(item)}</Typography>
                  </Stack>
                  <Stack spacing={1} direction="row">
                    <Tooltip arrow placement='top' title={`${item.note}`}>
                      <Typography variant="body2" textAlign={'justify'}>
                        { setLimitCharacter(item.note, (mdUp ? 530 : 74)) }
                      </Typography>
                    </Tooltip>
                  </Stack>
                  <Stack spacing={1} direction="row">
                    { mdUp && <Grid key={`labels_${index}`} container direction="row">
                      {item.labels.map(({ name, id }: any, key: any) =>
                        <Stack padding={0.5} key={key}>
                          <Label name={name} id={id} color="primary"/>
                        </Stack>
                      )}
                    </Grid> }
                  </Stack>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={2} md={1.3} sx={{ cursor: 'normal' }} textAlign={'center'}>
              <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, item, index)}>
                <Iconify icon={'eva:more-vertical-fill'}/>
              </IconButton>
            </Grid>
          </Grid>
        </Card>
      ))}
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75
            }
          }
        }}
      >
        <MenuItem onClick={() => {
          handleCloseMenu()
          onHandleCopy(athleteRoutine)
        }}>
          <Iconify icon={'eva:copy-fill'} sx={{ mr: 2 }}/>
          {t('routine.copyTo')}
        </MenuItem>

        <MenuItem onClick={() => {
          handleCloseMenu()
          onEdit(athleteRoutine)
        }}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }}/>
          {t('general.btn.edit')}
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={() => {
          handleCloseMenu()
          toggleDialog()
        }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }}/>
          {t('general.btn.delete')}
        </MenuItem>
      </Popover>
    </>
  )
}
